/**
 * Oxbo-M client — enterprise master data (well register + catalogs).
 *
 * Read-through cache posture: Oxbo-Z consults & caches, never writes. Base URL
 * is VITE_OXBO_M_URL (m.dev.oxbo.energy by default). In dev / standalone mode
 * MSW intercepts these calls and serves mock register + catalog data, so the
 * app runs with no live backend.
 *
 * Endpoints mirror the live contract exactly:
 *   GET /v1/wells                              — list the register
 *   GET /v1/wells/{well_id}                    — one well
 *   GET /v1/categories/{key}/resolved          — an effective-dated catalog
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  WellOut,
  ResolvedCatalogOut,
  WellView,
  CategoryOut,
  EntryOut,
  EntryCreate,
  EntryUpdate,
  WellboreOut,
  WellboreCreate,
  WellborePatch,
} from "./types";
import { toWellView } from "./mappers";
import { getDevToken } from "./devAuth";
import { getProdToken } from "./prodAuth";
import { getBrokerToken, isGlobalAdmin } from "./brokerAuth";

/**
 * The Bearer token to attach to Oxbo-M calls. Prefers the RCI-Auth broker token
 * (VITE_BROKER_AUTH prod build — real OIDC login); then the scripted prod-login
 * token (VITE_PROD_AUTH); then the dev-minted token (VITE_DEV_AUTH). In a
 * mocks-only build all are null and no Authorization header is sent (MSW answers
 * the call anyway).
 */
function getAuthToken(): string | null {
  return getBrokerToken() ?? getProdToken() ?? getDevToken();
}

/**
 * The Olympic Perú organization id. When an admin-mode super_admin creates a
 * catalog entry it must be org-scoped to Olympic, so POSTs carry this as
 * `admin_org_id`. (RCI staff act cross-tenant; the header alone doesn't pin the
 * target org.)
 */
export const OLYMPIC_ORG_ID = "0de70000-0000-4000-a000-000000000001";

/** Server-side filters the M contract accepts on /v1/wells. */
export interface WellListParams {
  status?: string;
  active_only?: boolean;
}

/**
 * POST /v1/wells body — the register-create contract. `uwi` + `common_name` are
 * required; the rest are optional identity/location fields. Admin-mode header is
 * injected by prepareHeaders when the caller is a global system-admin (the
 * backend dual-gates: 403 for non-admins, 409 on a duplicate uwi).
 */
export interface WellCreate {
  uwi: string;
  common_name: string;
  operator?: string | null;
  field?: string | null;
  site?: string | null;
  api14?: string | null;
  status?: string | null;
  lat?: number | null;
  lon?: number | null;
  elevation?: number | null;
}

const M_BASE_URL = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";

export const mApi = createApi({
  reducerPath: "mApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${M_BASE_URL}/v1`,
    // Inject the current JWT (real prod-login token, or dev-minted token). In a
    // mocks-only build both are null and no Authorization header is sent.
    prepareHeaders: (headers) => {
      const token = getAuthToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      // RCI cross-tenant oversight: a global system-admin opts into admin-mode
      // (cross-org reads). Backend still dual-gates (header AND global system role).
      if (isGlobalAdmin()) headers.set("X-OXBO-Admin-Mode", "true");
      return headers;
    },
  }),
  // Enterprise master data changes rarely — keep it cached generously.
  keepUnusedDataFor: 300,
  tagTypes: ["Well", "Catalog", "Wellbore"],
  endpoints: (builder) => ({
    // Wells come back as raw WellOut[]; we normalize to WellView[] here so the
    // whole UI works in one clean, unit-aware shape.
    getWells: builder.query<WellView[], WellListParams | void>({
      query: (params) => ({ url: "/wells", params: params ?? { active_only: false } }),
      transformResponse: (raw: WellOut[]) => raw.map(toWellView),
      providesTags: (result) =>
        result
          ? [
              ...result.map((w) => ({ type: "Well" as const, id: w.id })),
              { type: "Well" as const, id: "LIST" },
            ]
          : [{ type: "Well" as const, id: "LIST" }],
    }),
    getWell: builder.query<WellView, string>({
      query: (id) => `/wells/${id}`,
      transformResponse: (raw: WellOut) => toWellView(raw),
      providesTags: (_r, _e, id) => [{ type: "Well", id }],
    }),
    // Create a well in the register (Oxbo-M). Admin-only: prepareHeaders injects
    // X-OXBO-Admin-Mode for a global system-admin and the backend dual-gates (403
    // for non-admins, 409 on a duplicate uwi). Returns a WellOut we normalize to a
    // WellView; invalidating LIST refreshes the explorer/register + map so the new
    // well appears immediately.
    createWell: builder.mutation<WellView, WellCreate>({
      query: (body) => ({ url: "/wells", method: "POST", body }),
      transformResponse: (raw: WellOut) => toWellView(raw),
      invalidatesTags: [{ type: "Well", id: "LIST" }],
    }),
    // Coordinate editor (Ficha del pozo "Ubicación"). Merges lat/lon/elevation
    // into the well's `properties` bag server-side (other keys preserved) and
    // returns the updated WellOut, which we normalize back to a WellView. Admin
    // gate is enforced by Oxbo-M (403 → "Solo administradores" in the UI).
    // Invalidating the well tag + LIST refreshes the record AND the explorer map,
    // so a corrected well replots immediately.
    updateWellCoords: builder.mutation<
      WellView,
      { id: string; lat: number | null; lon: number | null; elevation?: number | null }
    >({
      query: ({ id, lat, lon, elevation }) => ({
        url: `/wells/${id}`,
        method: "PATCH",
        body: { lat, lon, ...(elevation !== undefined ? { elevation } : {}) },
      }),
      transformResponse: (raw: WellOut) => toWellView(raw),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Well", id },
        { type: "Well", id: "LIST" },
      ],
    }),
    // ── Wellbores (OH + sidetracks) ─────────────────────────────────────────
    // A well's wellbores: the Original Hole (OH) + any sidetracks. The Ficha del
    // pozo "Pozos perforados / Wellbores" section reads + edits these. The tag is
    // scoped to the well so a create/edit refreshes just that well's list.
    getWellbores: builder.query<WellboreOut[], string>({
      query: (wellId) => `/wells/${wellId}/wellbores`,
      providesTags: (_r, _e, wellId) => [{ type: "Wellbore", id: wellId }],
    }),
    // Create a wellbore (OH or a sidetrack) under a well. is_sidetrack drives the
    // type; a sidetrack requires parent_wellbore_id. Admin-gated on the backend
    // (same well-write gate as createWell). Invalidates the well's Wellbore tag.
    createWellbore: builder.mutation<WellboreOut, { wellId: string; body: WellboreCreate }>({
      query: ({ wellId, body }) => ({ url: `/wells/${wellId}/wellbores`, method: "POST", body }),
      invalidatesTags: (_r, _e, { wellId }) => [{ type: "Wellbore", id: wellId }],
    }),
    // Edit a wellbore (Pattern-L supersession server-side). Invalidates the well's
    // Wellbore tag so the list re-reads the new active version.
    updateWellbore: builder.mutation<
      WellboreOut,
      { wellId: string; wellboreId: string; body: WellborePatch }
    >({
      query: ({ wellId, wellboreId, body }) => ({
        url: `/wells/${wellId}/wellbores/${wellboreId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { wellId }) => [{ type: "Wellbore", id: wellId }],
    }),

    // Resolved catalog — e.g. activity codes, cost classes, vendors. Pickers
    // in the report screens read these. `asOf` supports effective-dating.
    getResolvedCatalog: builder.query<
      ResolvedCatalogOut,
      { categoryKey: string; asOf?: string; siteRegionKey?: string }
    >({
      query: ({ categoryKey, asOf, siteRegionKey }) => ({
        url: `/categories/${categoryKey}/resolved`,
        params: {
          ...(asOf ? { as_of: asOf } : {}),
          ...(siteRegionKey ? { site_region_key: siteRegionKey } : {}),
        },
      }),
      providesTags: (_r, _e, { categoryKey }) => [{ type: "Catalog", id: categoryKey }],
    }),

    // ── Picklist Admin (catalog curation, admin-mode super_admin) ──────────
    // The category headers (activity codes, NPT levels/categories, …). The
    // Picklist Admin screen groups these by category_group and lets an admin
    // edit the entries of a selected one.
    getCategories: builder.query<CategoryOut[], void>({
      query: () => "/categories",
    }),
    // The raw (un-resolved) entries of a category — the editable rows. Unlike
    // /resolved this is the source-of-truth list the admin curates.
    getEntries: builder.query<EntryOut[], string>({
      query: (categoryKey) => `/categories/${categoryKey}/entries`,
      providesTags: (_r, _e, categoryKey) => [{ type: "Catalog", id: categoryKey }],
    }),
    createEntry: builder.mutation<EntryOut, { categoryKey: string; body: EntryCreate }>({
      query: ({ categoryKey, body }) => ({
        url: `/categories/${categoryKey}/entries`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { categoryKey }) => [{ type: "Catalog", id: categoryKey }],
    }),
    updateEntry: builder.mutation<
      EntryOut,
      { categoryKey: string; id: string; body: EntryUpdate }
    >({
      query: ({ categoryKey, id, body }) => ({
        url: `/categories/${categoryKey}/entries/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { categoryKey }) => [{ type: "Catalog", id: categoryKey }],
    }),
    deleteEntry: builder.mutation<void, { categoryKey: string; id: string }>({
      query: ({ categoryKey, id }) => ({
        url: `/categories/${categoryKey}/entries/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { categoryKey }) => [{ type: "Catalog", id: categoryKey }],
    }),
  }),
});

export const {
  useGetWellsQuery,
  useGetWellQuery,
  useCreateWellMutation,
  useUpdateWellCoordsMutation,
  useGetWellboresQuery,
  useCreateWellboreMutation,
  useUpdateWellboreMutation,
  useGetResolvedCatalogQuery,
  useGetCategoriesQuery,
  useGetEntriesQuery,
  useCreateEntryMutation,
  useUpdateEntryMutation,
  useDeleteEntryMutation,
} = mApi;
